import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from 'reactstrap';
import { buildCoordinateFallbackLabel, loadGoogleMapsApi, reverseGeocodeLocation } from './GoogleMapAutoComplete';

const DEFAULT_CENTER = { lat: 30.23119, lng: -93.21163 };

const LocationMapPickerModal = ({
  isOpen,
  toggle,
  title,
  initialLocation,
  onConfirm,
}) => {
  const mapNodeRef = useRef(null);
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const clickListenerRef = useRef(null);
  const mountedRef = useRef(false);

  const [googleApi, setGoogleApi] = useState(null);
  const [isMapLoading, setIsMapLoading] = useState(false);
  const [mapError, setMapError] = useState('');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isResolvingAddress, setIsResolvingAddress] = useState(false);

  const initialCenter = useMemo(() => {
    if (initialLocation && typeof initialLocation.lat === 'number' && typeof initialLocation.lng === 'number') {
      return { lat: initialLocation.lat, lng: initialLocation.lng };
    }

    return DEFAULT_CENTER;
  }, [initialLocation]);

  useEffect(() => {
    mountedRef.current = true;

    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    let isMounted = true;
    setIsMapLoading(true);
    setMapError('');

    loadGoogleMapsApi()
      .then(google => {
        if (!isMounted || !mountedRef.current) {
          return;
        }

        setGoogleApi(google);
        setIsMapLoading(false);
      })
      .catch(error => {
        if (!isMounted || !mountedRef.current) {
          return;
        }

        setMapError(error && error.message ? error.message : 'Google Maps failed to load.');
        setIsMapLoading(false);
      });

    return () => {
      isMounted = false;
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) {
      setSelectedLocation(initialLocation || null);
      return;
    }

    setSelectedLocation(initialLocation || null);
  }, [initialLocation, isOpen]);

  useEffect(() => {
    if (!isOpen || !googleApi || !mapNodeRef.current || mapRef.current) {
      return undefined;
    }

    const nextMap = new googleApi.maps.Map(mapNodeRef.current, {
      center: initialCenter,
      zoom: initialLocation && typeof initialLocation.lat === 'number' && typeof initialLocation.lng === 'number' ? 15 : 11,
      streetViewControl: false,
      mapTypeControl: false,
      fullscreenControl: false,
    });

    mapRef.current = nextMap;

    const placeMarker = position => {
      if (!markerRef.current) {
        markerRef.current = new googleApi.maps.Marker({
          map: nextMap,
          position,
        });
        return;
      }

      markerRef.current.setPosition(position);
      markerRef.current.setMap(nextMap);
    };

    if (selectedLocation && typeof selectedLocation.lat === 'number' && typeof selectedLocation.lng === 'number') {
      placeMarker({ lat: selectedLocation.lat, lng: selectedLocation.lng });
    }

    clickListenerRef.current = nextMap.addListener('click', async event => {
      if (!event || !event.latLng) {
        return;
      }

      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      const nextPoint = { lat, lng };

      placeMarker(nextPoint);
      nextMap.panTo(nextPoint);
      if (mountedRef.current) {
        setIsResolvingAddress(true);
        setMapError('');
      }

      try {
        const normalized = await reverseGeocodeLocation({ lat, lng, google: googleApi });
        if (mountedRef.current) {
          setSelectedLocation(normalized);
        }
      } catch (error) {
        if (mountedRef.current) {
          setSelectedLocation({
            lat,
            lng,
            formattedAddress: buildCoordinateFallbackLabel(lat, lng),
            fallbackLabel: buildCoordinateFallbackLabel(lat, lng),
            address1: buildCoordinateFallbackLabel(lat, lng),
            address2: '',
            city: '',
            state: '',
            zip: '',
            country: '',
            countryCode: '',
          });
          setMapError('Address lookup was incomplete. The selected coordinates will still be saved.');
        }
      } finally {
        if (mountedRef.current) {
          setIsResolvingAddress(false);
        }
      }
    });

    return () => {
      if (clickListenerRef.current) {
        googleApi.maps.event.removeListener(clickListenerRef.current);
        clickListenerRef.current = null;
      }

      if (markerRef.current) {
        markerRef.current.setMap(null);
        markerRef.current = null;
      }

      mapRef.current = null;
    };
  }, [googleApi, initialCenter, initialLocation, isOpen, selectedLocation]);

  useEffect(() => {
    if (!isOpen || !mapRef.current || !googleApi) {
      return;
    }

    const placeMarker = position => {
      if (!markerRef.current) {
        markerRef.current = new googleApi.maps.Marker({
          map: mapRef.current,
          position,
        });
        return;
      }

      markerRef.current.setPosition(position);
      markerRef.current.setMap(mapRef.current);
    };

    if (selectedLocation && typeof selectedLocation.lat === 'number' && typeof selectedLocation.lng === 'number') {
      const nextPoint = { lat: selectedLocation.lat, lng: selectedLocation.lng };
      placeMarker(nextPoint);
      mapRef.current.panTo(nextPoint);
      return;
    }

    if (markerRef.current) {
      markerRef.current.setMap(null);
      markerRef.current = null;
    }
  }, [googleApi, isOpen, selectedLocation]);

  const handleConfirm = () => {
    if (!selectedLocation || typeof selectedLocation.lat !== 'number' || typeof selectedLocation.lng !== 'number') {
      setMapError('Choose a location on the map before confirming.');
      return;
    }

    onConfirm(selectedLocation);
    toggle();
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered fade={false} autoFocus={false} returnFocusAfterClose={false}>
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <ModalBody>
        <div style={{ marginBottom: 12 }}>
          <p style={{ marginBottom: 6, color: '#6c757d' }}>Click the map to place a pin. If no street address is found, the dashboard will save the coordinates with a custom location label.</p>
          {selectedLocation && (
            <div style={{ fontSize: 14, color: '#212529' }}>
              <strong>Selected location:</strong> {selectedLocation.formattedAddress || selectedLocation.address1 || buildCoordinateFallbackLabel(selectedLocation.lat, selectedLocation.lng)}
            </div>
          )}
        </div>
        {mapError ? <Alert color="warning" style={{ marginBottom: 12 }}>{mapError}</Alert> : null}
        <div
          style={{
            width: '100%',
            height: 420,
            borderRadius: 12,
            overflow: 'hidden',
            background: '#f2f4f7',
            position: 'relative'
          }}
        >
          <div
            ref={mapNodeRef}
            style={{
              position: 'absolute',
              inset: 0,
            }}
          />
          {(isMapLoading || isResolvingAddress) && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'rgba(255, 255, 255, 0.75)',
                zIndex: 1,
                gap: 10,
                color: '#344675'
              }}
            >
              <Spinner size="sm" />
              <span>{isResolvingAddress ? 'Resolving address...' : 'Loading map...'}</span>
            </div>
          )}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button color="secondary" onClick={toggle}>Cancel</Button>
        <Button color="info" onClick={handleConfirm}>Use This Location</Button>
      </ModalFooter>
    </Modal>
  );
};

LocationMapPickerModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  toggle: PropTypes.func.isRequired,
  title: PropTypes.string,
  initialLocation: PropTypes.shape({
    lat: PropTypes.number,
    lng: PropTypes.number,
    formattedAddress: PropTypes.string,
  }),
  onConfirm: PropTypes.func.isRequired,
};

LocationMapPickerModal.defaultProps = {
  title: 'Select Location',
  initialLocation: null,
};

export default LocationMapPickerModal;